import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { axiosPrivate } from "@/api/axios";

const wellnessSchema = z.object({
  title: z.string().min(1, "Title is required"),
  type: z.string().min(1, "Type is required"),
  stepsText: z.string().min(1, "At least one step is required"),
  duration: z
    .string()
    .min(1, "Duration is required")
    .regex(/^[0-9]+$/, "Duration must be a number"),
  benefits: z.string().min(1, "Benefits are required"),
});

type WellnessForm = z.infer<typeof wellnessSchema>;

interface WellnessActivity {
  id: number;
  title: string;
  type: string;
  steps: string[];
  duration: string;
  benefits: string;
}

interface AddWellnessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (activity: WellnessActivity) => void;
  onUpdated?: (activity: WellnessActivity) => void;
  defaultType?: string; // e.g., "exercise"
  lockType?: boolean; // Lock the type field (read-only)
  editMode?: boolean; // Edit mode vs create mode
  editData?: {
    id: number;
    title: string;
    type: string;
    steps: string[];
    duration: string;
    benefits: string;
  };
}

export default function AddWellnessDialog({ 
  open, 
  onOpenChange, 
  onCreated, 
  onUpdated,
  defaultType = "exercise", 
  lockType = false,
  editMode = false,
  editData
}: AddWellnessDialogProps) {
  const [submitting, setSubmitting] = React.useState(false);

  const resolvedType = React.useMemo(() => {
    return (editMode && editData?.type) ? editData.type : defaultType;
  }, [defaultType, editData?.type, editMode]);

  const dialogCopy = React.useMemo(() => {
    const base = {
      label: "Wellness Activity",
      createDescription: "Create a new Wellness Activity (Admin only).",
      updateDescription: "Update the wellness activity details.",
    };

    switch (resolvedType) {
      case "exercise":
        return {
          label: "Exercise Activity",
          createDescription: "Create a new Exercise Activity (Admin only).",
          updateDescription: "Update the exercise activity details.",
        };
      case "therapy":
        return {
          label: "Emotional Therapy",
          createDescription: "Create a new Daily Emotional Therapy Activity.",
          updateDescription: "Update the emotional therapy details.",
        };
      case "stress":
        return {
          label: "Stress Management Activity",
          createDescription: "Create a new Daily Stress Management Activity.",
          updateDescription: "Update the stress management activity details.",
        };
      default:
        return base;
    }
  }, [resolvedType]);

  const dialogTitle = editMode ? `Edit ${dialogCopy.label}` : `Create ${dialogCopy.label}`;
  const dialogDescription = editMode ? dialogCopy.updateDescription : dialogCopy.createDescription;

  const titlePlaceholder = React.useMemo(() => {
    switch (resolvedType) {
      case "exercise":
        return "Enter exercise activity title...";
      case "therapy":
        return "Enter emotional therapy title...";
      case "stress":
        return "Enter stress management title...";
      default:
        return "Enter wellness activity title...";
    }
  }, [resolvedType]);

  const benefitsPlaceholder = React.useMemo(() => {
    switch (resolvedType) {
      case "exercise":
        return "Describe the key benefits of this exercise activity...";
      case "therapy":
        return "Describe the key benefits of this therapy activity...";
      case "stress":
        return "Describe the key benefits of this stress management activity...";
      default:
        return "Describe the key benefits of this wellness activity...";
    }
  }, [resolvedType]);

  const stepsPlaceholder = React.useMemo(() => {
    switch (resolvedType) {
      case "exercise":
        return "Warm up for five minutes\nPerform the main exercise routine\nCool down and stretch";
      case "therapy":
        return "Practice deep breathing\nJournal three positive thoughts\nShare feelings with a friend";
      case "stress":
        return "Identify stress triggers\nApply a calming technique\nReflect on the outcome";
      default:
        return "Start with deep breathing\nPerform the main activity\nCool down and relax";
    }
  }, [resolvedType]);

  const extractNumericDuration = React.useCallback((value?: string) => {
    if (!value) return "";
    const match = value.match(/([0-9]+)/);
    return match ? match[1] : "";
  }, []);

  const form = useForm<WellnessForm>({
    resolver: zodResolver(wellnessSchema),
    defaultValues: {
      title: "",
      type: defaultType,
      stepsText: "",
      duration: "",
      benefits: "",
    },
    mode: "onChange",
  });

  // Populate form when editing
  React.useEffect(() => {
    if (editMode && editData) {
      form.reset({
        title: editData.title,
        type: editData.type,
        stepsText: editData.steps.join("\n"),
        duration: extractNumericDuration(editData.duration),
        benefits: editData.benefits,
      });
    } else if (!editMode) {
      form.reset({
        title: "",
        type: defaultType,
        stepsText: "",
        duration: "",
        benefits: "",
      });
    }
  }, [defaultType, editData, editMode, extractNumericDuration, form]);

  async function handleSubmit(values: WellnessForm) {
    try {
      setSubmitting(true);
      // Convert stepsText (multiline) -> string[]
      const steps = values.stepsText
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);

      const numericDuration = values.duration.replace(/[^0-9]/g, "");

      const payload = {
        title: values.title.trim(),
        type: values.type.trim() || "exercise",
        steps,
        duration: numericDuration ? `${numericDuration} minutes` : "",
        benefits: values.benefits.trim(),
      };

      if (editMode && editData) {
        // Update existing activity
        const res = await axiosPrivate.put(`/wellness/${editData.id}/`, payload);
        toast.success("Wellness activity updated");
        onUpdated?.(res?.data ?? payload);
      } else {
        // Create new activity
        const res = await axiosPrivate.post("/wellness/", payload);
        toast.success("Wellness activity created");
        onCreated?.(res?.data ?? payload);
      }
      
      onOpenChange(false);
      form.reset({ title: "", type: defaultType, stepsText: "", duration: "", benefits: "" });
    } catch (err: any) {
      const msg = err?.response?.data?.message || (editMode ? "Failed to update wellness activity" : "Failed to create wellness activity");
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] sm:!w-full !max-w-[600px] !max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField name="title" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Title
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder={titlePlaceholder} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField name="type" control={form.control} render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Type
                    <span className="text-destructive ml-1">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="exercise, therapy, stress..." {...field} readOnly={lockType} disabled={lockType} className={lockType ? "bg-muted cursor-not-allowed" : ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />

              <FormField
                name="duration"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Duration (minutes)
                      <span className="text-destructive ml-1">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="flex">
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          {...field}
                          value={field.value ?? ""}
                          onChange={(event) => {
                            const numericValue = event.target.value.replace(/[^0-9]/g, "");
                            field.onChange(numericValue);
                          }}
                          className="rounded-r-none"
                          placeholder="Enter duration"
                        />
                        <span className="inline-flex items-center px-3 border border-l-0 rounded-r-md text-sm bg-muted text-muted-foreground">
                          minutes
                        </span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField name="benefits" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Benefits
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea rows={3} placeholder={benefitsPlaceholder} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField name="stepsText" control={form.control} render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Steps (one per line)
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl>
                  <Textarea rows={6} placeholder={stepsPlaceholder} {...field} />
                </FormControl>
                <p className="text-xs text-muted-foreground">Each line will be sent as one step.</p>
                <FormMessage />
              </FormItem>
            )} />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="w-full sm:w-auto" disabled={!form.formState.isValid || submitting}>
                {submitting ? (editMode ? "Updating…" : "Creating…") : (editMode ? "Update Wellness" : "Create Wellness")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
